import React, { useState } from 'react';
import axios from 'axios';
import { Brain, Globe, Loader2, Sparkles } from 'lucide-react';
import { API } from '@/App';

/**
 * AI-generated breeding-context interpretation card. Renders below
 * any completed analysis to surface a 1-paragraph English or Hindi
 * summary of the result.
 *
 * Extracted from DesignUploadCard.js (P2 refactor, Feb 2026).
 */
export function InsightPanel({ analysisId, designLabel, color: _color }) {
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('en');

  const fetchInsight = async (targetLang) => {
    setLoading(true);
    setLang(targetLang);
    try {
      let summary = `${designLabel} analysis completed. Analysis ID: ${analysisId}.`;
      try {
        const { data } = await axios.get(`${API}/analysis/${analysisId}`, { withCredentials: true });
        const results = data?.results || {};
        const keys = Object.keys(results);
        const firstKey = keys[0];
        if (firstKey) {
          const r = results[firstKey];
          const snippets = [];
          if (r.interpretation) snippets.push(r.interpretation);
          if (r.anova_table || r.tables?.anova) snippets.push('ANOVA computed');
          if (r.gca_effects || r.tables?.gca_effects) {
            const gca = r.gca_effects || r.tables?.gca_effects;
            if (Array.isArray(gca)) snippets.push('GCA: ' + gca.map(g => `${g.Parent}=${g.GCA_effect}`).join(', '));
            else if (typeof gca === 'object') snippets.push('GCA: ' + Object.entries(gca).map(([k, v]) => `${k}=${v}`).join(', '));
          }
          if (r.variance_components || r.tables?.variance_components) snippets.push('Variance components computed');
          if (r.genetic_conclusions || r.tables?.genetic_parameters) snippets.push('Genetic parameters estimated');
          summary = snippets.join('. ') || summary;
        }
      } catch { /* fall through to default summary */ }
      const res = await axios.post(`${API}/ai-insight`, {
        analysis_type: designLabel,
        summary: summary.slice(0, 1500),
        lang: targetLang,
      }, { withCredentials: true });
      setInsight(res.data?.insight || res.data?.error || 'Could not generate insight.');
    } catch {
      setInsight('Failed to generate insight. Please try again.');
    }
    setLoading(false);
  };

  if (!analysisId) return null;

  return (
    <div className="mt-5 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="insight-panel">
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #F0FFF4, #F0F7F3)', borderBottom: '1px solid var(--border-color)' }}>
        <span className="font-body flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)' }}>
          <Brain size={14} style={{ color: 'var(--green-700)' }} /> BK Intelligence Insight
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => fetchInsight('en')}
            disabled={loading}
            className="font-body flex items-center gap-1 btn-press transition-all"
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 'var(--radius-sm)', fontWeight: 500,
              background: lang === 'en' && insight ? 'var(--green-700)' : 'white',
              color: lang === 'en' && insight ? 'white' : 'var(--text-medium)',
              border: `1px solid ${lang === 'en' && insight ? 'var(--green-700)' : 'var(--border-color)'}`,
            }}
            data-testid="insight-en"
          >
            {loading && lang === 'en' ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />} English
          </button>
          <button
            onClick={() => fetchInsight('hi')}
            disabled={loading}
            className="font-body flex items-center gap-1 btn-press transition-all"
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 'var(--radius-sm)', fontWeight: 500,
              background: lang === 'hi' && insight ? '#D97706' : 'white',
              color: lang === 'hi' && insight ? 'white' : 'var(--text-medium)',
              border: `1px solid ${lang === 'hi' && insight ? '#D97706' : 'var(--border-color)'}`,
            }}
            data-testid="insight-hi"
          >
            <Globe size={9} /> Hindi
          </button>
        </div>
      </div>
      {loading && (
        <div className="px-4 py-4 text-center bg-white">
          <Loader2 size={16} className="animate-spin mx-auto" style={{ color: 'var(--green-500)' }} />
          <p className="font-body mt-1" style={{ fontSize: 11, color: 'var(--text-light)' }}>Generating insight...</p>
        </div>
      )}
      {insight && !loading && (
        <div className="px-4 py-4 bg-white">
          <p className="font-body" style={{ fontSize: 14, color: 'var(--text-dark)', lineHeight: 1.7, ...(lang === 'hi' ? { fontFamily: 'Noto Sans Devanagari, sans-serif' } : {}) }}>{insight}</p>
        </div>
      )}
      {!insight && !loading && (
        <div className="px-4 py-4 bg-white text-center">
          <p className="font-body" style={{ fontSize: 12, color: 'var(--text-light)' }}>Click English or Hindi to generate an AI interpretation of your results</p>
        </div>
      )}
    </div>
  );
}

export default InsightPanel;
